import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaEdit, FaTrash } from 'react-icons/fa';
import PurchaseOrderForm from './PurchaseOrderForm';
import ReactPaginate from 'react-paginate';
import { Link, useLocation } from 'react-router-dom';
import PurchaseOrderDetails from './PurchaseOrderDetails';
import Swal from 'sweetalert2';
import { Container, Card, Tabs, Tab, Button } from 'react-bootstrap';
import { BiPencil } from "react-icons/bi";
import { BsChevronLeft } from "react-icons/bs";
import { useNavigate } from 'react-router-dom';

const PurchaseOrderList = ({ quotationId }) => {
    const [purchaseorders, setPurchaseOrders] = useState([]);
    const navigate = useNavigate();

    const [showModal, setShowModal] = useState(false);
    const [selectedPurchaseOrderId, setSelectedPurchaseOrderId] = useState(null);
    const [selectedPurchaseOrderData, setSelectedPurchaseOrderData] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [activeTab, setActiveTab] = useState('poList'); // Active tab state
    const [setupIdError, setSetupIdError] = useState('');
    const [errors, setErrors] = useState({});
    const [quotationData, setQuotationData] = useState(null);

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const quotation_id = queryParams.get('quotation_id') || ''; // Use prop or query parameter

    const isPurchaseOrderDetailsPage = location.pathname.includes('/purchase-order/details/');

    useEffect(() => {
        if (!isPurchaseOrderDetailsPage && quotation_id && currentPage === 0) {
            fetchPurchaseOrders(1); // Fetch orders only if we're on the list page
        }
    }, [quotation_id, currentPage, isPurchaseOrderDetailsPage]);


    const fetchPurchaseOrders = async (page = 1) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/purchase-order?quotation_id=${quotation_id}&page=${page}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
            });

            const { data, current_page, last_page } = response.data.purchaseorders;
            setPurchaseOrders(data);
            setCurrentPage(current_page - 1);
            setPageCount(last_page);
            const fetchedQuotationData = response?.data?.purchaseorders?.data[0]?.quotation;
            if (fetchedQuotationData) {
                setQuotationData(fetchedQuotationData);  // Set quotation data into state
                console.log('Fetched Quotation Data:', fetchedQuotationData);
            } else {
                console.log('Quotation data not found in the response');
            }
        } catch (error) {
            console.error('Error fetching purchaseorders:', error);
        }
    };


    const deletePurchaseOrder = async (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const backendUrl = process.env.REACT_APP_BACKEND_URL;
                    const userInfo = JSON.parse(localStorage.getItem('user-info'));
                    const origin = localStorage.getItem('user-origin');
                    const token = userInfo?.token;

                    await axios.delete(`${backendUrl}/api/purchase-order/${id}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Tenant-Origin': origin,
                        },
                    });

                    setPurchaseOrders(purchaseorders.filter(purchaseorder => purchaseorder.id !== id));
                    Swal.fire(
                        "Deleted!",
                        "The purchase order has been deleted.",
                        "success"
                    );
                } catch (error) {
                    console.error('Error deleting purchase order:', error);
                    Swal.fire(
                        "Error!",
                        "Failed to delete purchase order. Please try again.",
                        "error"
                    );
                }
            }
        });

    };


    const handleShowModal = (poId = null) => {
        if (poId) {
            const purchaseorderToEdit = purchaseorders.find(purchaseorder => purchaseorder.id === poId);
            if (purchaseorderToEdit) {
                setSelectedPurchaseOrderData(purchaseorderToEdit);
            }
        } else {
            setSelectedPurchaseOrderData(null);
        }
        setSelectedPurchaseOrderId(poId);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedPurchaseOrderId(null);
        setSelectedPurchaseOrderData(null);
        setSetupIdError('');
        setErrors({});
    };



    const handlePageClick = (data) => {
        const selectedPage = data.selected + 1;
        fetchPurchaseOrders(selectedPage);
    };

    const handleTabClick = (tab) => {
        setActiveTab(tab);
    };

    const handleBackClick = () => {
        // Example back navigation, you can customize this as needed
        window.history.back();
    };

    const calculateTotalPurchaseOrderAmount = () => {
        return purchaseorders.reduce((total, po) => total + parseFloat(po.amount || 0), 0).toFixed(2);
    };

    const calculatePOSplits = (po) => {
        const subscription = po.vendor_subscriptions?.[0];
        if (!subscription || !subscription.vendordue_dates) return [];

        return subscription.vendordue_dates.map((d, index) => ({
            due_date: d.due_date.split('T')[0],
            amount: parseFloat(d.amount).toFixed(2),
            status: d.is_paid ? 'Paid' : 'Pending', // <-- now shows correctly
            split_no: index + 1
        }));
    };



    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    {/* Page Title */}
                    <div className="row">
                        <div className="col-12">
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                <h4 className="mb-sm-0  d-flex align-items-center">
                                    <BsChevronLeft
                                        className='back-button1'
                                        onClick={() => navigate(-1)}
                                    />
                                    <span>Purchase Order</span>
                                </h4>
                                <div className="page-title-right"></div>
                            </div>
                        </div>
                    </div>
                    <div className="p-6 max-w-7xl mx-auto">
                        <>
                            {purchaseorders && purchaseorders.length > 0 ? (
                                <div className="row">
                                    {/* Left Side - Project & Client Summary + Total Proforma Amount */}
                                    <div className="col-md-12">
                                        <Card className="mb-4 shadow-lg rounded-3 p-3 border-0 card-bg">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <Card.Title className="fw-bold font-size-14 text-primary">
                                                    {quotationData?.ref_no || ' '}
                                                </Card.Title>

                                            </div>

                                            <Card.Body className="pt-3">
                                                <div className="row">
                                                    {/* Client Details */}
                                                    <div className="col-md-6">
                                                        <div className="mb-3">
                                                            <span className="text-muted d-block card-label"><strong>Client:</strong></span>
                                                            <span className="text-muted card-value">{quotationData?.project?.client?.name || '-'}</span>
                                                        </div>
                                                        <div className="mb-3">
                                                            <span className="text-muted d-block card-label"><strong>Project:</strong></span>
                                                            <span className="text-muted card-value">{quotationData?.project?.name || '-'}</span>
                                                        </div>

                                                    </div>

                                                    {/* Proforma Total */}
                                                    <div className="col-md-4">
                                                        <div className="mb-3">
                                                            <span className="text-muted d-block card-label"><strong>Total Amount:</strong></span>
                                                            <span className="fw-semibold text-success card-value">
                                                                {calculateTotalPurchaseOrderAmount()}                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </div>


                                </div>
                            ) : (
                                <div className="text-center text-muted">No Purchase Order found.</div>
                            )}
                        </>
                        <div className="row">
                            <div className="col-xl-12 col-lg-8">
                                <div className="card">
                                    <ul className="nav nav-tabs nav-tabs-custom justify-content-start pt-2 font-size-13" role="tablist">
                                        <li className="nav-item">
                                            <a
                                                className={`nav-link ${activeTab === 'poList' ? 'active' : ''}`}
                                                onClick={() => setActiveTab('poList')}
                                                role="tab"
                                            >
                                                Purchase Order
                                            </a>
                                        </li>

                                        <li className="nav-item">
                                            <a
                                                className={`nav-link ${activeTab === 'poSplits' ? 'active' : ''}`}
                                                onClick={() => setActiveTab('poSplits')}
                                                role="tab"
                                            >
                                                PO Splits
                                            </a>
                                        </li>

                                    </ul>
                                    <div className="tab-content p-4">
                                        {/* Contact List Tab */}
                                        {activeTab === "poList" && (
                                            <div className="tab-pane active" id="contact-list" role="tabpanel">
                                                <div className="p-3">
                                                    <div>
                                                        <br />
                                                        <table className="table table-hover datatable dt-responsive nowrap view-table font-size-13" >
                                                            <thead>
                                                                <tr>
                                                                    <th scope="col">#</th>
                                                                    <th scope="col">Reference Number</th>
                                                                    <th scope="col">Date</th>
                                                                    <th scope="col">Vendor</th>
                                                                    <th scope="col">Amount</th>
                                                                    <th scope="col">Action</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {Array.isArray(purchaseorders) && purchaseorders.length > 0 ? (
                                                                    purchaseorders.map((purchaseorder, index) => (
                                                                        <tr key={purchaseorder.id}>
                                                                            <td>{index + 1}</td>
                                                                            <td><Link to={`/purchase-order/details/${purchaseorder.id}`} className="dropdown-item">{purchaseorder.ref_no}</Link></td>
                                                                            <td>{purchaseorder.date}</td>
                                                                            <td>{purchaseorder.product_item_details?.length > 0
                                                                                ? purchaseorder.product_item_details[0]?.product?.vendor?.name
                                                                                : 'Vendor Not Available'}</td>
                                                                            <td>{purchaseorder.amount}</td>
                                                                            <td>
                                                                                {/* Action buttons for Edit and Delete */}
                                                                                <div className="dropdown">
                                                                                    <i
                                                                                        className="mdi mdi-dots-horizontal font-size-18"
                                                                                        data-bs-toggle="dropdown"
                                                                                        aria-expanded="false"
                                                                                        style={{ cursor: 'pointer' }}
                                                                                    ></i>
                                                                                    <ul className="dropdown-menu dropdown-menu-end font-size-14">
                                                                                        <li>
                                                                                            <Link to={`/purchase-order/details/${purchaseorder.id}`} className="dropdown-item">
                                                                                                View Details
                                                                                            </Link>
                                                                                        </li>
                                                                                        <li>
                                                                                            <button
                                                                                                className="dropdown-item"
                                                                                                onClick={() => handleShowModal(purchaseorder.id)}
                                                                                            >
                                                                                                Edit
                                                                                            </button>
                                                                                        </li>
                                                                                        <li>
                                                                                            <button
                                                                                                className="dropdown-item"
                                                                                                onClick={() => deletePurchaseOrder(purchaseorder.id)}
                                                                                            >
                                                                                                Delete
                                                                                            </button>
                                                                                        </li>
                                                                                    </ul>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    ))
                                                                ) : (
                                                                    <tr>
                                                                        <td colSpan="6" className="text-center">No purchase orders</td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                        <div className="d-flex justify-content-end pagination-sm">
                                                            <ReactPaginate
                                                                previousLabel={'<'}
                                                                nextLabel={'>'}
                                                                breakLabel={'...'}
                                                                pageCount={pageCount}
                                                                marginPagesDisplayed={2}
                                                                pageRangeDisplayed={3}
                                                                onPageChange={handlePageClick}
                                                                containerClassName={'pagination pagination-rounded justify-content-end mb-2'}
                                                                pageClassName={'page-item'}
                                                                pageLinkClassName={'page-link'}
                                                                previousClassName={'page-item previous'}
                                                                previousLinkClassName={'page-link'}
                                                                nextClassName={'page-item next'}
                                                                nextLinkClassName={'page-link'}
                                                                breakClassName={'page-item'}
                                                                breakLinkClassName={'page-link'}
                                                                activeClassName={'active'}
                                                            />
                                                        </div>
                                                    </div>
                                                </div></div>
                                        )}
                                        {activeTab === "poSplits" && (
                                            <div className="tab-pane active" id="po-splits" role="tabpanel">
                                                <div className="p-3">
                                                    <table className="table table-hover datatable dt-responsive nowrap view-table font-size-13">
                                                        <thead>
                                                            <tr>
                                                                <th>Split #</th>

                                                                <th>PO Ref</th>
                                                                <th>Due Date</th>
                                                                <th>Amount</th>
                                                                <th>Status</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {purchaseorders.map((po, poIndex) => {
                                                                const splits = calculatePOSplits(po);
                                                                return splits.map((split, splitIndex) => (
                                                                    <tr key={`${po.id}-${splitIndex}`}>
                                                                        <td>{splitIndex + 1}</td>

                                                                        <td>{po.ref_no}</td>
                                                                        <td>{split.due_date}</td>
                                                                        <td>{split.amount}</td>
                                                                        <td>
                                                                            <span className={`badge ${split.status === 'Paid' ? 'bg-success' : 'bg-warning'}`}>
                                                                                {split.status}
                                                                            </span>
                                                                        </td>
                                                                    </tr>
                                                                ));
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}

                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>


            <PurchaseOrderForm
                showModal={showModal}
                handleClose={handleCloseModal}
                poId={selectedPurchaseOrderId}
                purchaseorderData={selectedPurchaseOrderData}
                setPurchaseOrders={setPurchaseOrders}
            />
        </div>
    );
};





export default PurchaseOrderList;
